/**
 * Defensive coordinate recovery — shared helper for the admin app.
 *
 * A Location was observed in the DB with `lng = -7987638282685921`
 * (i.e. `-79.87638282685921` with the decimal stripped). The write-path
 * bug that produced that value hasn't been root-caused yet; until it is,
 * every admin surface that reads or writes coordinates normalizes them
 * defensively:
 *
 *   - On READ (map widgets): decimal-shift out-of-range values so the
 *     marker plots. Emit a console.warn so the bad record is visible.
 *   - On WRITE (edit-form submit): normalize before PUT so the server's
 *     validateCoordinates doesn't 400 on a value that only existed
 *     because of the same upstream bug the admin is now trying to fix.
 *
 * Centralised here so the two call sites (DashboardPage destination map,
 * LocationDetailPage edit form) can't drift on the recovery rule.
 */
export function normalizeLatLng(
  raw: { lat?: number; lng?: number } | undefined | null,
  contextName: string,
): { lat: number; lng: number } | null {
  if (!raw || typeof raw.lat !== 'number' || typeof raw.lng !== 'number') return null;
  if (!isFinite(raw.lat) || !isFinite(raw.lng)) return null;

  let { lat, lng } = raw;
  let fixed = false;
  let guard = 0;
  while (Math.abs(lng) > 180 && guard < 18) { lng /= 10; guard += 1; fixed = true; }
  guard = 0;
  while (Math.abs(lat) > 90 && guard < 18) { lat /= 10; guard += 1; fixed = true; }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  if (fixed) {
    // eslint-disable-next-line no-console
    console.warn(
      `[admin/coords] Recovered mangled coordinates for ${contextName}`,
      { original: raw, normalized: { lat, lng } },
    );
  }
  return { lat, lng };
}
